
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFindSuppliers } from "../../actions/sector/sectorActions";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Flex, Icon } from "@chakra-ui/react";

const FindSuppliers = ({ productSearchTerm, locationSearchTerm, checkedProducts }) => {
  const { sectorId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isHovered, setIsHovered] = useState(false);


  const sectorState = useSelector(
    (state) =>
      state.sectorSupplierReducer || {
        data: [],
        loading: false,
        error: null,
      }
  );

  const { data: nearbySuppliers = [], loading = false, error = null } = sectorState;
  const { isAuthenticatedUser } = useSelector((state) => state.userReducer);

  useEffect(() => {
    if (sectorId) {
      dispatch(getFindSuppliers(sectorId));
    }
  }, [sectorId, dispatch]);

  // ---- Utility to clean up location ----
  const extractLocation = (address) => {
    if (!address || typeof address !== "string") return "";

    const stateMatch = address.match(/([A-Za-z\s]+)\s+India$/i);
    if (stateMatch) {
      return stateMatch[1].trim() + " India";
    }

    const parts = address
      .split(",")
      .map((p) => p.replace(/-\s*\d+$/, "").trim())
      .filter(Boolean);

    if (parts.length > 0) {
      for (let i = parts.length - 1; i >= 0; i--) {
        const segment = parts[i];
        if (!/^\d+$/.test(segment) && segment.length > 2) {
          return segment;
        }
      }
    }
    return "";
  };

  // ---- Icons ----
  const LocationIcon = (props) => (
    <Icon viewBox="0 0 512 512" {...props}>
      <path
        fill="white"
        stroke="gray"
        strokeWidth="32"
        d="M256 0C167.6 0 96 71.6 96 160c0 112 160 352 160 352s160-240 160-352c0-88.4-71.6-160-160-160zm0 240c-44.2 0-80-35.8-80-80s35.8-80 80-80
           80 35.8 80 80-35.8 80-80 80z"
      />
    </Icon>
  );

  const CostIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
      <path
        fill="teal"
        d="M16.5 6c.3-.4.5-.9.5-1.5 0-1.1-.9-2-2-2h-6c-1.1 0-2 .9-2 2 0 .6.2 1.1.5 1.5C5.6 7.6 4 10.6 4 14c0 4.4 3.6 8 8 8s8-3.6 8-8c0-3.4-1.6-6.4-3.5-8zM13 17.9V19h-2v-1.1c-1.1-.2-2-.9-2-2h1.8c0 .6.5 1.1 1.2 1.1.7 0 1.2-.3 1.2-.9 0-.3-.2-.6-.6-.7l-1.4-.3c-1.1-.2-2-.9-2-2.2 0-1.2.9-2.1 2-2.3V9h2v1.1c1.1.2 2 .9 2 2h-1.8c0-.6-.5-1.1-1.2-1.1-.7 0-1.2.3-1.2.9 0 .3.2.6.6.7l1.4.3c1.1.2 2 .9 2 2.2 0 1.3-.9 2.2-2 2.4z"
      />
    </Icon>
  );

  // ---- Process suppliers ----
  const safeSuppliers = Array.isArray(nearbySuppliers) ? nearbySuppliers : [];
  const mappedSuppliers = safeSuppliers.map((supplier) => ({
    ...supplier,
    location: extractLocation(supplier.companyRegdOffice),
  }));

  const filteredSuppliers = mappedSuppliers.filter((supplier) => {
    const productNames = Array.isArray(supplier.products)
      ? supplier.products.map((p) => p?.product?.toLowerCase())
      : [];

    const matchesCheckedProducts =
      checkedProducts.length === 0 ||
      productNames.some((p) => checkedProducts.map((c) => c.toLowerCase()).includes(p));

    const matchesProductSearch =
      !productSearchTerm ||
      productNames.some((p) => p.includes(productSearchTerm.toLowerCase()));

    const matchesLocation = (supplier.location || "")
      .toLowerCase()
      .includes((locationSearchTerm || "").toLowerCase());

    return matchesCheckedProducts && matchesProductSearch && matchesLocation;
  });

  // ---- Styles ----
  const styles = {
    container: {
      padding: "20px",
      fontFamily: "Arial, sans-serif",
      display: "flex",
      gap: "20px",
    },
    mainContent: {
      flex: 1,
      paddingLeft: "20px",
      height: "calc(100vh - 120px)",
      overflowY: "auto",
      paddingRight: "8px",
    },
    supplierGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "20px",
      width: "100%",
    },
    supplierCard: {
      border: "1px solid #e0e0e0",
      borderRadius: "4px",
      padding: "16px",
      marginBottom: "16px",
      backgroundColor: "white",
    },
    logo: {
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      backgroundColor: "#f0f0f0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: "12px",
      fontSize: "12px",
    },
    header: {
      display: "flex",
      alignItems: "center",
      marginBottom: "12px",
    },
    companyName: {
      fontSize: "16px",
      fontWeight: "bold",
      marginRight: "12px",
    },
    productLabel: {
      color: "#1E1E1E",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "14px",
      fontWeight: "600",
      margin: "4px",
    },
    productCnt: {
      color: "#1E1E1E",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "14px",
      fontWeight: "400",
      margin: "4px",
    },
    categoryTag: {
      backgroundColor: "#f5f5f5",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      margin: "4px",
    },
    loading: {
      textAlign: "center",
      padding: "20px",
    },
    error: {
      color: "red",
      textAlign: "center",
      padding: "20px",
    },
    noSuppliers: {
      textAlign: "center",
      padding: "20px",
      color: "#666",
    },
  };

  // ---- Supplier Card ----
  const SupplierCard = ({ supplier }) => (
    <div style={styles.supplierCard}>
      <div style={styles.header}>
        <div style={styles.logo}>
          {supplier.logo ||
            (supplier.companyName ? supplier.companyName.substring(0, 3) : "N/A")}
        </div>
        <div style={{ flex: 1 }}>
          <span style={styles.companyName}>{supplier.companyName}</span>
        </div>
      </div>

      <Flex align="center" gap={5}>
        <LocationIcon boxSize={5} color="gray.600" />
        <span>{supplier.location}</span>

        <Flex align="center" gap={4}>
          <CostIcon boxSize={6} order={0} />
          <Box fontWeight={600} fontSize={14} order={1}>
            Cost Efficient
          </Box>
        </Flex>
      </Flex>

      <Box>
        <Flex justifyContent={"space-between"}>
          <Box style={styles.productLabel}>Products Available</Box>
          <Box style={styles.productCnt}>
            {supplier.products.length} product
            {supplier.products.length > 1 ? "s" : ""} found
          </Box>
        </Flex>

        <Flex flexWrap={"wrap"}>
          {supplier.products.map((p, i) => (
            <Box key={i} style={styles.categoryTag}>
              {p.product}
            </Box>
          ))}
        </Flex>

        <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
          <button
            onClick={() => navigate(`/client/${supplier._id}/sme-background`)}
            style={{
              backgroundColor: "#30D5C8",
              color: "black",
              border: "1px solid #30D5C8",
              borderRadius: "4px",
              fontWeight: "600",
              cursor: "pointer",
              padding: "2px 25px",
              width: "100%",
              transition: "background-color 0.3s, border-color 0.3s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#FFAA00";
              e.currentTarget.style.borderColor = "#FFAA00";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#30D5C8";
              e.currentTarget.style.borderColor = "#30D5C8";
            }}
          >
            View SMM Profile
          </button>
        </div>
      </Box>
    </div>
  );


  if (loading) return <div style={styles.loading}>Loading suppliers...</div>;
  if (error) return <div style={styles.error}>Error: {error}</div>;

  return (
    <div style={styles.container}>
      <main style={styles.mainContent}>
        <section style={{ marginTop: "10px" }}>
          <h2 style={{ fontWeight: 600 }}>SUPPLIERS NEAR YOU</h2>


          <Box
            className="supplier-grid-wrapper"
            data-isautherised={isAuthenticatedUser ? "true" : "false"}
            style={{
              marginTop: isAuthenticatedUser ? "0px" : "125px",
            }}
          >
            <div style={styles.supplierGrid}>
              {filteredSuppliers.length > 0 ? (
                <>
                  {isAuthenticatedUser ? (

                    filteredSuppliers.map((supplier, index) => (
                      <SupplierCard key={index} supplier={supplier} />
                    ))
                  ) : (

                    <>
                      {filteredSuppliers.slice(0, 2).map((supplier, index) => (
                        <SupplierCard key={index} supplier={supplier} />
                      ))}

                      {filteredSuppliers.slice(2, 4).map((supplier, index) => (
                        <div
                          key={`locked-${index}`}
                          style={{
                            filter: "blur(4px)",
                            pointerEvents: "none",
                            opacity: 0.6,
                            transition: "all 0.3s ease",
                          }}
                        >
                          <SupplierCard supplier={supplier} />
                        </div>
                      ))}
                    </>
                  )}
                </>
              ) : (
                <div style={styles.noSuppliers}>No suppliers found</div>
              )}
            </div>
          </Box>

        </section>
      </main>
    </div>
  );
};

export default FindSuppliers;

